// FOUNDER-OS query options: the switcher's list (under `["me"]`, so a `/v1/me` refresh refetches
// it) and the portfolio views (under `["company", co, "portfolio"]`, so a `founder_brief` ping or
// any portfolio write refetches them). A drill-down reads a company through that company's own
// routes and keys, as the founder's own membership or grant there.
import { ApiError } from "@companyos/sdk";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export const portfolioKeys = {
  root: (co: string) => ["company", co, "portfolio"] as const,
  focus: (co: string) => ["company", co, "portfolio", "focus"] as const,
  chart: (co: string) => ["company", co, "portfolio", "chart"] as const,
  escalations: (co: string) => ["company", co, "portfolio", "escalations"] as const,
  goals: (co: string) => ["company", co, "portfolio", "goals"] as const,
  briefs: (co: string) => ["company", co, "portfolio", "briefs"] as const,
  companies: (co: string) => ["company", co, "portfolio", "companies"] as const,
  // FOUNDER-OS phase 1: the roll-up grid (KPIs › Scorecard) and Reports › Live.
  scorecard: (co: string) => ["company", co, "portfolio", "scorecard"] as const,
  live: (co: string) => ["company", co, "portfolio", "live"] as const,
};

export const meCompaniesQuery = (api: Api) =>
  queryOptions({
    queryKey: ["me", "companies"] as const,
    // An API without the route yet answers 404: null, and the switcher keeps `/v1/me`'s list
    // (no toast: nothing failed that the person asked for).
    queryFn: async () => {
      try {
        return await api.client.me.companies();
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    staleTime: 30_000,
    retry: false,
  });

export const focusQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: portfolioKeys.focus(co),
    queryFn: () => api.client.portfolio.focus({ params: { co } }),
  });

export const portfolioChartQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: portfolioKeys.chart(co),
    queryFn: () => api.client.portfolio.chart({ params: { co } }),
  });

export const escalationsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: portfolioKeys.escalations(co),
    queryFn: () => api.client.portfolio.escalations({ params: { co } }),
  });

export const portfolioGoalsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: portfolioKeys.goals(co),
    queryFn: () => api.client.portfolio.goals({ params: { co } }),
  });

export const briefsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: portfolioKeys.briefs(co),
    queryFn: () => api.client.portfolio.briefs.list({ params: { co } }),
  });

export const portfolioScorecardQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: portfolioKeys.scorecard(co),
    queryFn: () => api.client.portfolio.scorecard({ params: { co } }),
  });

export const portfolioLiveQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: portfolioKeys.live(co),
    queryFn: () => api.client.portfolio.live({ params: { co } }),
  });

/**
 * FOUNDER-OS: the caller may make account-wide writes (add a company, cascade, generate a brief,
 * move a card): an owner grant on the whole account, as the chart's `can_edit` says (a grant
 * limited to some companies reads only). Shares the chart's cached read.
 */
export function useCanEditPortfolio(api: Api, co: string): boolean {
  return useQuery(portfolioChartQuery(api, co)).data?.can_edit === true;
}

/**
 * FOUNDER-OS phase 1: hang a company's linked card under another seat of the founder chart
 * (`id`: the link, or the company when it has none yet). The owner only.
 */
export function useMoveLinkedSeat(api: Api, co: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; portfolio_seat_id: string }) =>
      api.client.portfolio.linkedSeats.update({
        params: { co, id: v.id },
        body: { portfolio_seat_id: v.portfolio_seat_id },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: portfolioKeys.chart(co) }),
  });
}

export const portfolioCompaniesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: portfolioKeys.companies(co),
    queryFn: () => api.client.portfolio.companies.list({ params: { co } }),
  });
